import { z } from "zod";

/**
 * Shared vocabulary for agent-assisted shopping on a real merchant's website.
 *
 * The agent browses, assembles a cart, and *proposes* what it believes the
 * checkout says. Nothing here is trusted on the agent's word: the host re-reads
 * the same page through its own readers and compares against this proposal
 * before a purchase can be approved, and again before any card is filled.
 */

/**
 * A card fill requires HTTPS without embedded credentials, and every frame a
 * binding names is held to this page's own origin.
 */
const httpsUrl = z
  .string()
  .url()
  .max(8192)
  .refine((value) => {
    try {
      const url = new URL(value);
      return url.protocol === "https:" && !url.username && !url.password;
    } catch {
      return false;
    }
  }, "must be an https URL without embedded credentials");

export const shoppingAddress = z.object({
  name: z.string().trim().max(255).default(""),
  line1: z.string().trim().max(255).default(""),
  line2: z.string().trim().max(255).default(""),
  city: z.string().trim().max(255).default(""),
  region: z.string().trim().max(255).default(""),
  postalCode: z.string().trim().max(32).default(""),
  country: z.string().trim().max(2).default(""),
});
export type ShoppingAddress = z.infer<typeof shoppingAddress>;

export const shoppingCustomer = z.object({
  email: z.string().trim().max(255).default(""),
  phone: z.string().trim().max(64).default(""),
  shipping: shoppingAddress.default(() => shoppingAddress.parse({})),
  billing: shoppingAddress.default(() => shoppingAddress.parse({})),
});
export type ShoppingCustomer = z.infer<typeof shoppingCustomer>;

/**
 * Recurring terms are carried for every quote, including one-time ones, so the
 * verifier can reject a checkout that turns out to renew when nobody said it would.
 */
export const shoppingTerms = z.object({
  kind: z.enum(["oneTime", "subscription"]),
  interval: z.string().trim().max(100).optional(),
  renewalAmount: z.string().trim().max(100).optional(),
  trial: z.string().trim().max(500).optional(),
  cancellation: z.string().trim().max(1000).optional(),
});
export type ShoppingTerms = z.infer<typeof shoppingTerms>;

export const shoppingItem = z.object({
  name: z.string().trim().min(1).max(500),
  variant: z.string().trim().max(500).optional(),
  quantity: z.number().int().positive().max(100),
});
export type ShoppingItem = z.infer<typeof shoppingItem>;

export const shoppingQuote = z.object({
  merchant: z.string().trim().min(1).max(255),
  checkoutUrl: httpsUrl,
  items: z.array(shoppingItem).min(1).max(30),
  /** Link permits at most 50000 minor units per spend request. */
  amountMinor: z.number().int().nonnegative().max(50_000),
  currency: z.string().regex(/^[a-z]{3}$/),
  shipping: z.string().trim().max(500),
  tax: z.string().trim().max(500),
  discount: z.string().trim().max(500),
  delivery: z.string().trim().max(500),
  customer: shoppingCustomer,
  terms: shoppingTerms,
});
export type ShoppingQuote = z.infer<typeof shoppingQuote>;

/**
 * A locator is inert data the host interpolates into its own fixed browser
 * program. The agent never supplies executable code, and a selector that
 * matches anything other than exactly one visible element fails the read.
 */
export const shoppingLocator = z.object({
  selector: z.string().min(1).max(1000),
  frameUrl: httpsUrl.optional(),
});
export type ShoppingLocator = z.infer<typeof shoppingLocator>;

const shoppingFact = shoppingLocator.extend({
  expected: z.string().trim().min(1).max(2000),
});

export const shoppingCustomerField = z.enum([
  "email",
  "phone",
  "shipping.name",
  "shipping.line1",
  "shipping.line2",
  "shipping.city",
  "shipping.region",
  "shipping.postalCode",
  "shipping.country",
  "billing.name",
  "billing.line1",
  "billing.line2",
  "billing.city",
  "billing.region",
  "billing.postalCode",
  "billing.country",
]);

export const shoppingBindings = z.object({
  merchant: shoppingFact,
  total: shoppingFact,
  currency: shoppingFact,
  items: z
    .array(
      z.object({
        name: shoppingFact,
        quantity: shoppingFact,
        variant: shoppingFact.optional(),
      }),
    )
    .min(1)
    .max(30),
  shipping: shoppingFact,
  tax: shoppingFact,
  discount: shoppingFact,
  delivery: shoppingFact,
  terms: shoppingFact,
  customer: z
    .array(
      shoppingLocator.extend({
        field: shoppingCustomerField,
        control: z.enum(["input", "select"]),
      }),
    )
    .max(30),
  /** Card fields are named for Kernel's vault fill operation, never for values. */
  card: z
    .array(
      shoppingLocator.extend({
        field: z.enum(["number", "cvc", "expiration", "exp_month", "exp_year"]),
        /** Kernel requires this on a combined expiration field and forbids it elsewhere. */
        format: z.enum(["MM/YY", "MM/YYYY"]).optional(),
      }),
    )
    .min(3)
    .max(4)
    .superRefine((fields, ctx) => {
      const fail = (message: string) => ctx.addIssue({ code: z.ZodIssueCode.custom, message });
      const count = (name: string) => fields.filter((field) => field.field === name).length;
      for (const required of ["number", "cvc"]) {
        if (count(required) !== 1) fail(`the checkout needs exactly one ${required} field`);
      }
      const combined = count("expiration");
      const split = count("exp_month") + count("exp_year");
      if (!((combined === 1 && split === 0) || (combined === 0 && split === 2))) {
        fail("expiry must be one combined field or both exp_month and exp_year");
      }
      for (const field of fields) {
        // Kernel rejects the request outright when format appears anywhere else.
        if ((field.field === "expiration") !== Boolean(field.format)) {
          fail("only a combined expiration field carries a format");
        }
      }
      // Kernel requires that no two bindings resolve to the same element.
      const targets = fields.map((field) => `${field.frameUrl ?? ""}|${field.selector}`);
      if (new Set(targets).size !== targets.length) {
        fail("two card fields point at the same element");
      }
    })
    .optional(),
  submit: shoppingLocator,
  /** Some checkouts ask an agent to declare itself; the host ticks it truthfully. */
  disclosure: shoppingLocator.optional(),
});
export type ShoppingBindings = z.infer<typeof shoppingBindings>;

export const shoppingProposal = z.object({
  quote: shoppingQuote,
  bindings: shoppingBindings,
  /** A merchant-owned order history that the host can read before and after checkout. */
  verification: z
    .object({
      kind: z.literal("schemaOrgOrder"),
      historyUrl: httpsUrl,
    })
    .optional(),
});
export type ShoppingProposal = z.infer<typeof shoppingProposal>;

/**
 * What the merchant itself says about the order afterwards. A success banner or
 * a completed click is not evidence; only a merchant order record is.
 */
export const shoppingOrderEvidence = z.object({
  orderId: z.string().trim().min(1).max(255),
  url: z.string().url().max(8192),
  merchant: z.string().trim().min(1).max(255),
  amountMinor: z.number().int().nonnegative(),
  currency: z.string().regex(/^[a-z]{3}$/),
  items: z.array(shoppingItem),
  state: z.enum(["paid", "unpaid", "pending", "subscriptionActive"]),
  terms: shoppingTerms.optional(),
  observedAt: z.string().datetime(),
  source: z.enum(["merchantRecord", "support"]),
});
export type ShoppingOrderEvidence = z.infer<typeof shoppingOrderEvidence>;

export const shoppingHandoffPurpose = z.enum(["checkout", "authentication", "subscription"]);
export type ShoppingHandoffPurpose = z.infer<typeof shoppingHandoffPurpose>;

export const shoppingPaymentMethod = z.object({
  id: z.string().min(1).max(255),
  label: z.string().min(1).max(255),
});

/**
 * The client-facing projection of a purchase. Deliberately excludes the
 * bindings, the browser id, and every provider handle — a card renders from
 * this, so anything here is visible to whoever can see the thread.
 */
export const shoppingSummary = z.object({
  catalogOrderId: z.string().uuid().optional(),
  revision: z.number().int().positive(),
  quote: shoppingQuote.nullable(),
  customer: shoppingCustomer,
  paymentMethodId: z.string().optional(),
  paymentMethods: z.array(shoppingPaymentMethod),
  verified: z.boolean(),
  order: shoppingOrderEvidence.optional(),
  handoffPurpose: shoppingHandoffPurpose.optional(),
  missingFields: z.array(shoppingCustomerField).default([]),
  /** The checkout binds billing-address fields, so the card must offer them. */
  billingRequired: z.boolean().default(false),
  canEdit: z.boolean(),
  canApprove: z.boolean(),
  canHandoff: z.boolean(),
  canOpenProviderApproval: z.boolean().optional(),
  canCancel: z.boolean(),
});
export type ShoppingSummary = z.infer<typeof shoppingSummary>;

/**
 * How many minor units make a major one for this currency.
 *
 * Not every currency is two decimal places: JPY has none and BHD has three.
 * The card the user approves and the parser that reads the merchant's total
 * must agree on this, or the figure someone consents to is not the figure that
 * gets charged.
 */
export function minorUnitDigits(currency: string): number {
  try {
    return (
      new Intl.NumberFormat("en", {
        style: "currency",
        currency: currency.toUpperCase(),
      }).resolvedOptions().maximumFractionDigits ?? 2
    );
  } catch {
    return 2;
  }
}

export function formatMinorAmount(amountMinor: number, currency: string): string {
  const value = amountMinor / 10 ** minorUnitDigits(currency);
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency.toUpperCase(),
    }).format(value);
  } catch {
    return `${value.toFixed(minorUnitDigits(currency))} ${currency.toUpperCase()}`;
  }
}

export const shoppingActionId = z.enum(["approve", "cancel", "recheck", "continue"]);
export type ShoppingActionId = z.infer<typeof shoppingActionId>;

/**
 * Approval names the revision it approves. A card rendered before an edit
 * therefore cannot approve the cart that replaced it.
 */
export const shoppingActionReq = z.object({
  action: shoppingActionId,
  revision: z.number().int().positive(),
  tempClientId: z.string().max(255).optional(),
});
export type ShoppingActionReq = z.infer<typeof shoppingActionReq>;
